import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, setDoc, arrayUnion, increment } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';

const TCoinsContext = createContext();

export const useTCoins = () => {
  const context = useContext(TCoinsContext);
  if (!context) {
    throw new Error('useTCoins must be used within a TCoinsProvider');
  }
  return context;
};

// T Coins Types
export const TCOINS_TYPES = {
  // Bonus Coins (เหรียญโบนัส) - แอดมินแจก, Campaign, แลกเงินไม่ได้
  VIRTUAL: 'virtual',  // เหรียญโบนัส - แลกเงินไม่ได้
  
  // T Coins (เหรียญจริง) - ซื้อจากแอดมิน, แลกเงินได้
  REAL: 'real'         // เหรียญจริง - แลกเงินได้
};

// T Coins Categories
export const TCOINS_CATEGORIES = {
  VIRTUAL: 'virtual',  // เหรียญโบนัส
  REAL: 'real'         // เหรียญจริง
};

// T Coins Colors
export const TCOINS_COLORS = {
  [TCOINS_TYPES.VIRTUAL]: '#FF6B6B',  // สีแดง - เหรียญโบนัส
  [TCOINS_TYPES.REAL]: '#4ECDC4'      // สีเขียว - เหรียญจริง
};

// T Coins Icons
export const TCOINS_ICONS = {
  [TCOINS_TYPES.VIRTUAL]: '🎁',  // ของขวัญ - เหรียญโบนัส
  [TCOINS_TYPES.REAL]: '💰'      // เงิน - เหรียญจริง
};

// T Coins Names
export const TCOINS_NAMES = {
  [TCOINS_TYPES.VIRTUAL]: 'Bonus Coins',
  [TCOINS_TYPES.REAL]: 'T Coins'
};

// Exchange Rate
export const EXCHANGE_RATE = {
  VIRTUAL_TO_MONEY: 1,    // 1 Bonus Coins = 1 บาท (ใช้ในระบบ)
  REAL_TO_MONEY: 1,       // 1 T Coins = 1 บาท (แลกเงินได้)
  MIN_WITHDRAWAL: 300,    // ขั้นต่ำถอน 300 T Coins
  FEE_PERCENTAGE: 5,      // ค่าธรรมเนียม 5%
  FEE_MINIMUM: 15         // ค่าธรรมเนียมขั้นต่ำ 15 T Coins
};

export const TCoinsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [tcoins, setTcoins] = useState({
    virtual: 0,  // Bonus Coins (เหรียญโบนัส)
    real: 0      // T Coins (เหรียญจริง)
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Auto-load T Coins when user logs in
  useEffect(() => {
    if (currentUser?.uid) {
      loadTCoins(currentUser.uid);
    } else {
      setTcoins({ virtual: 0, real: 0 });
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  // Load T Coins from Firestore
  const loadTCoins = async (userId) => {
    try {
      setLoading(true);
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setTcoins({
          virtual: userData.tcoins?.virtual || 0,  // Bonus Coins
          real: userData.tcoins?.real || 0         // T Coins
        });
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading T Coins:', err);
    } finally {
      setLoading(false);
    }
  };

  // Add T Coins
  const addTCoins = async (userId, type, amount, reason = '') => {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      // Check if user document exists
      if (userSnap.exists()) {
        // User document exists, update it
        await updateDoc(userRef, {
          [`tcoins.${type}`]: increment(amount),
          updatedAt: new Date()
        });
      } else {
        // User document doesn't exist, create it
        const currentTcoins = { virtual: 0, real: 0 };
        currentTcoins[type] = amount;
        
        await setDoc(userRef, {
          uid: userId,
          tcoins: currentTcoins,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // Update local state
      setTcoins(prev => ({
        ...prev,
        [type]: prev[type] + amount
      }));

      // Log transaction
      await logTransaction(userId, 'add', type, amount, reason);

      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error adding T Coins:', err);
      return false;
    }
  };

  // Deduct T Coins
  const deductTCoins = async (userId, type, amount, reason = '') => {
    try {
      if (tcoins[type] < amount) {
        throw new Error(`T Coins ${type} ไม่เพียงพอ`);
      }

      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      // Check if user document exists
      if (userSnap.exists()) {
        // User document exists, update it
        await updateDoc(userRef, {
          [`tcoins.${type}`]: increment(-amount),
          updatedAt: new Date()
        });
      } else {
        // User document doesn't exist, create it with 0 coins
        const currentTcoins = { virtual: 0, real: 0 };
        currentTcoins[type] = Math.max(0, (currentTcoins[type] || 0) - amount);
        
        await setDoc(userRef, {
          uid: userId,
          tcoins: currentTcoins,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      // Update local state
      setTcoins(prev => ({
        ...prev,
        [type]: prev[type] - amount
      }));

      // Log transaction
      await logTransaction(userId, 'deduct', type, amount, reason);

      return true;
    } catch (err) {
      setError(err.message);
      console.error('Error deducting T Coins:', err);
      return false;
    }
  };

  // Get total exchangeable T Coins (only real coins)
  const getTotalExchangeable = () => {
    return tcoins.real;  // เฉพาะ T Coins เท่านั้นที่แลกเงินได้
  };

  // Get total T Coins (including virtual)
  const getTotalTCoins = () => {
    return tcoins.virtual + tcoins.real;
  };

  // Get total Virtual T Coins
  const getTotalVirtual = () => {
    return tcoins.virtual;
  };

  // Get total Real T Coins
  const getTotalReal = () => {
    return tcoins.real;
  };

  // Calculate exchange value
  const getExchangeValue = () => {
    const total = getTotalExchangeable();
    const fee = Math.max(total * EXCHANGE_RATE.FEE_PERCENTAGE / 100, EXCHANGE_RATE.FEE_MINIMUM);
    return {
      total,
      fee,
      net: total - fee,
      canExchange: total >= EXCHANGE_RATE.MIN_WITHDRAWAL
    };
  };

  // Log transaction
  const logTransaction = async (userId, type, coinType, amount, reason) => {
    try {
      const transaction = {
        id: Date.now().toString(),
        type, // 'add' or 'deduct'
        coinType,
        amount,
        reason,
        timestamp: new Date(),
        balance: tcoins[coinType]
      };

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        transactions: arrayUnion(transaction)
      });
    } catch (err) {
      console.error('Error logging transaction:', err);
    }
  };

  // Daily bonus
  const claimDailyBonus = async (userId) => {
    try {
      const today = new Date().toDateString();
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const lastClaim = userData.lastDailyBonus;
        
        if (lastClaim !== today) {
          // Give daily bonus (Bonus Coins)
          await addTCoins(userId, TCOINS_TYPES.VIRTUAL, 10, 'Daily Bonus');
          
          // Update last claim date
          await updateDoc(userRef, {
            lastDailyBonus: today
          });
          
          return true;
        }
      }
      return false;
    } catch (err) {
      setError(err.message);
      console.error('Error claiming daily bonus:', err);
      return false;
    }
  };

  // Check if can claim daily bonus
  const canClaimDailyBonus = async (userId) => {
    try {
      const today = new Date().toDateString();
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        return userData.lastDailyBonus !== today;
      }
      return true;
    } catch (err) {
      console.error('Error checking daily bonus:', err);
      return false;
    }
  };

  const value = {
    tcoins,
    loading,
    error,
    loadTCoins,
    addTCoins,
    deductTCoins,
    getTotalExchangeable,
    getTotalTCoins,
    getTotalVirtual,
    getTotalReal,
    getExchangeValue,
    claimDailyBonus,
    canClaimDailyBonus,
    TCOINS_TYPES,
    TCOINS_CATEGORIES,
    TCOINS_COLORS,
    TCOINS_ICONS,
    TCOINS_NAMES,
    EXCHANGE_RATE
  };

  return (
    <TCoinsContext.Provider value={value}>
      {children}
    </TCoinsContext.Provider>
  );
};

export default TCoinsContext;

